import axios from 'axios';
import { API_BASE_URL } from '../../config/apiConfig';

const base = API_BASE_URL;

// ─────────────────────────────────────────────────────────────────────────────
// Shared lookups (DCA / Sub-DCA / States)
// ─────────────────────────────────────────────────────────────────────────────

export const getDCAForHRRules = async () => {
    try {
        const response = await axios.get(`${base}/HR/GetDCAForHRRules`);
        return response.data;
    } catch (error) {
        if (error.response?.data) throw error.response.data;
        throw error.message || 'Failed to fetch DCA list';
    }
};

export const getSubDCAByDCA = async (DCACode) => {
    try {
        const response = await axios.get(`${base}/Accounts/GetSubDCAbyDCA`, { params: { DCACode } });
        return response.data;
    } catch (error) {
        if (error.response?.data) throw error.response.data;
        throw error.message || 'Failed to fetch Sub DCA list';
    }
};

export const getCostCenterStatesList = async () => {
    try {
        const response = await axios.get(`${base}/HR/GetCostCenterStatesList`);
        return response.data;
    } catch (error) {
        if (error.response?.data) throw error.response.data;
        throw error.message || 'Failed to fetch states list';
    }
};

export const getAllEmpCostCenters = async () => {
    try {
        const response = await axios.get(`${base}/HR/GetAllEmpCostCenters`);
        return response.data;
    } catch (error) {
        if (error.response?.data) throw error.response.data;
        throw error.message || 'Failed to fetch cost centres';
    }
};

// ─────────────────────────────────────────────────────────────────────────────
// 1. GROUPS
// ─────────────────────────────────────────────────────────────────────────────

export const getAllEmpGroups = async () => {
    try {
        const response = await axios.get(`${base}/HR/GetAllEmpGroups`);
        return response.data;
    } catch (error) {
        if (error.response?.data) throw error.response.data;
        throw error.message || 'Failed to fetch groups';
    }
};

/** Action: 'New' | 'Delete' (deactivate) | 'Activate' */
export const saveNewEmpGroup = async (payload) => {
    try {
        const response = await axios.post(`${base}/HR/SaveNewEmpGroup`, payload);
        return response.data;
    } catch (error) {
        if (error.response?.data) throw error.response.data;
        throw error.message || 'Failed to save group';
    }
};

// ─────────────────────────────────────────────────────────────────────────────
// 2. EMPLOYEE CATEGORY
// ─────────────────────────────────────────────────────────────────────────────

export const getAllEmpCategories = async () => {
    try {
        const response = await axios.get(`${base}/HR/GetAllEmpCategories`);
        return response.data;
    } catch (error) {
        if (error.response?.data) throw error.response.data;
        throw error.message || 'Failed to fetch employee categories';
    }
};

export const saveNewEmpCategory = async ({ EmpCategorysData, CreatedBy }) => {
    try {
        const response = await axios.post(`${base}/HR/SaveNewEmpCategory`, { EmpCategorysData, CreatedBy });
        return response.data;
    } catch (error) {
        if (error.response?.data) throw error.response.data;
        throw error.message || 'Failed to save employee category';
    }
};

export const updateEmpCategory = async ({ EmpCategorysData, CreatedBy }) => {
    try {
        const response = await axios.put(`${base}/HR/UpdateEmpCategory`, { EmpCategorysData, CreatedBy });
        return response.data;
    } catch (error) {
        if (error.response?.data) throw error.response.data;
        throw error.message || 'Failed to update employee category';
    }
};

// ─────────────────────────────────────────────────────────────────────────────
// 3. SALARY / MIN WAGES — two sub-features (StaffType:'Staff' salary heads,
//    StaffType:'Labour' min wages) sharing SaveHRSalaryRules / DeleteHRSalaryHeadMinWage
// ─────────────────────────────────────────────────────────────────────────────

export const getGroupForSalaryRule = async () => {
    try {
        const response = await axios.get(`${base}/HR/GetGroupForSalaryRule`);
        return response.data;
    } catch (error) {
        if (error.response?.data) throw error.response.data;
        throw error.message || 'Failed to fetch groups';
    }
};

/** Heads='None' returns the full salary-head master list */
export const getAllSalaryHeads = async (Heads = 'None') => {
    try {
        const response = await axios.get(`${base}/HR/GetAllSalaryHeads`, { params: { Heads } });
        return response.data;
    } catch (error) {
        if (error.response?.data) throw error.response.data;
        throw error.message || 'Failed to fetch salary heads';
    }
};

export const getSalaryHeadType = async (Headname) => {
    try {
        const response = await axios.get(`${base}/HR/GetSalaryHeadType`, { params: { Headname } });
        return response.data;
    } catch (error) {
        if (error.response?.data) throw error.response.data;
        throw error.message || 'Failed to fetch salary head type';
    }
};

export const saveNewSalaryHead = async (payload) => {
    try {
        const response = await axios.post(`${base}/HR/SaveNewSalaryHead`, payload);
        return response.data;
    } catch (error) {
        if (error.response?.data) throw error.response.data;
        throw error.message || 'Failed to save new salary head';
    }
};

export const getAllStaffSalryHeads = async () => {
    try {
        const response = await axios.get(`${base}/HR/GetAllStaffSalryHeads`);
        return response.data;
    } catch (error) {
        if (error.response?.data) throw error.response.data;
        throw error.message || 'Failed to fetch salary head rules';
    }
};

export const getCostCenterStatesForMinWage = async (Skilltype) => {
    try {
        const response = await axios.get(`${base}/HR/GetCostCenterStatesForMinWage`, { params: { Skilltype } });
        return response.data;
    } catch (error) {
        if (error.response?.data) throw error.response.data;
        throw error.message || 'Failed to fetch states for skill type';
    }
};

export const getAllMinWageRule = async () => {
    try {
        const response = await axios.get(`${base}/HR/GetAllMinWageRule`);
        return response.data;
    } catch (error) {
        if (error.response?.data) throw error.response.data;
        throw error.message || 'Failed to fetch minimum wage rules';
    }
};

/** payload.StaffType: 'Staff' (salary heads) | 'Labour' (min wages) */
export const saveHRSalaryRules = async (payload) => {
    try {
        const response = await axios.post(`${base}/HR/SaveHRSalaryRules`, payload);
        return response.data;
    } catch (error) {
        if (error.response?.data) throw error.response.data;
        throw error.message || 'Failed to save salary rule';
    }
};

export const deleteHRSalaryHeadMinWage = async (payload) => {
    try {
        const response = await axios.put(`${base}/HR/DeleteHRSalaryHeadMinWage`, payload);
        return response.data;
    } catch (error) {
        if (error.response?.data) throw error.response.data;
        throw error.message || 'Failed to delete salary rule';
    }
};

// ─────────────────────────────────────────────────────────────────────────────
// 4. PF & ESI  (singleton config — flat PFESIRules body, not wrapped)
// ─────────────────────────────────────────────────────────────────────────────

export const getPFESIRuleData = async () => {
    try {
        const response = await axios.get(`${base}/HR/GetPFESIRuleData`);
        return response.data;
    } catch (error) {
        if (error.response?.data) throw error.response.data;
        throw error.message || 'Failed to fetch PF/ESI rule';
    }
};

export const saveHRPFESIRule = async (payload) => {
    try {
        const response = await axios.post(`${base}/HR/SaveHRPFESIRule`, payload);
        return response.data;
    } catch (error) {
        if (error.response?.data) throw error.response.data;
        throw error.message || 'Failed to save PF/ESI rule';
    }
};

// ─────────────────────────────────────────────────────────────────────────────
// 5. HOLIDAYS
// ─────────────────────────────────────────────────────────────────────────────

export const getAllHolidays = async () => {
    try {
        const response = await axios.get(`${base}/HR/GetAllHolidays`);
        return response.data;
    } catch (error) {
        if (error.response?.data) throw error.response.data;
        throw error.message || 'Failed to fetch holidays';
    }
};

export const saveNewHoliday = async ({ HolidaysData, CreatedBy }) => {
    try {
        const response = await axios.post(`${base}/HR/SaveNewHoliday`, { HolidaysData, CreatedBy });
        return response.data;
    } catch (error) {
        if (error.response?.data) throw error.response.data;
        throw error.message || 'Failed to save holiday';
    }
};

export const deleteHoliday = async ({ HolidaysData, CreatedBy }) => {
    try {
        const response = await axios.put(`${base}/HR/DeleteHoliday`, { HolidaysData, CreatedBy });
        return response.data;
    } catch (error) {
        if (error.response?.data) throw error.response.data;
        throw error.message || 'Failed to delete holiday';
    }
};

// ─────────────────────────────────────────────────────────────────────────────
// 6. EL / PAID LEAVE
// ─────────────────────────────────────────────────────────────────────────────

export const getEmpLeaveTypes = async () => {
    try {
        const response = await axios.get(`${base}/HR/GetEmpLeaveTypes`);
        return response.data;
    } catch (error) {
        if (error.response?.data) throw error.response.data;
        throw error.message || 'Failed to fetch leave types';
    }
};

export const getLeaveRuleYears = async () => {
    try {
        const response = await axios.get(`${base}/HR/GetLeaveRuleYears`);
        return response.data;
    } catch (error) {
        if (error.response?.data) throw error.response.data;
        throw error.message || 'Failed to fetch leave rule years';
    }
};

export const getGroupsPaidLeaves = async ({ Leavetypeid, year }) => {
    try {
        const response = await axios.get(`${base}/HR/GetGroupsPaidLeaves`, { params: { Leavetypeid, year } });
        return response.data;
    } catch (error) {
        if (error.response?.data) throw error.response.data;
        throw error.message || 'Failed to fetch groups for paid leave';
    }
};

export const checkCustomLeaveExistForPageload = async () => {
    try {
        const response = await axios.get(`${base}/HR/CheckCustomLeaveExistForPageload`);
        return response.data;
    } catch (error) {
        if (error.response?.data) throw error.response.data;
        throw error.message || 'Failed to check existing EL/Paid leave rule';
    }
};

export const checkCustomLeaveExist = async ({ leavetypeid, GroupId, Year }) => {
    try {
        const response = await axios.get(`${base}/HR/CheckCustomLeaveExist`, { params: { leavetypeid, GroupId, Year } });
        return response.data;
    } catch (error) {
        if (error.response?.data) throw error.response.data;
        throw error.message || 'Failed to validate EL/Paid leave rule';
    }
};

export const getAllLPaidLeavesData = async () => {
    try {
        const response = await axios.get(`${base}/HR/GetAllLPaidLeavesData`);
        return response.data;
    } catch (error) {
        if (error.response?.data) throw error.response.data;
        throw error.message || 'Failed to fetch EL/Paid leave rules';
    }
};

export const saveELorPaidLeaveRule = async ({ ELorPaidLeaveRuleData, CreatedBy }) => {
    try {
        const response = await axios.post(`${base}/HR/SaveELorPaidLeaveRule`, { ELorPaidLeaveRuleData, CreatedBy });
        return response.data;
    } catch (error) {
        if (error.response?.data) throw error.response.data;
        throw error.message || 'Failed to save EL/Paid leave rule';
    }
};

export const deleteELPaidLeave = async ({ ELorPaidLeaveRuleData, CreatedBy }) => {
    try {
        const response = await axios.put(`${base}/HR/DeleteELPaidLeave`, { ELorPaidLeaveRuleData, CreatedBy });
        return response.data;
    } catch (error) {
        if (error.response?.data) throw error.response.data;
        throw error.message || 'Failed to delete EL/Paid leave rule';
    }
};

// ─────────────────────────────────────────────────────────────────────────────
// 7. GRATUITY  (singleton — flat GratuityEligibility body)
// ─────────────────────────────────────────────────────────────────────────────

export const getGratuityRuleData = async () => {
    try {
        const response = await axios.get(`${base}/HR/GetGratuityeRuleData`);
        return response.data;
    } catch (error) {
        if (error.response?.data) throw error.response.data;
        throw error.message || 'Failed to fetch gratuity rule';
    }
};

export const saveHRGratuityRule = async (payload) => {
    try {
        const response = await axios.post(`${base}/HR/SaveHRGratuityRule`, payload);
        return response.data;
    } catch (error) {
        if (error.response?.data) throw error.response.data;
        throw error.message || 'Failed to save gratuity rule';
    }
};

// ─────────────────────────────────────────────────────────────────────────────
// 8. ADVANCE (LTA & Salary Advance)
// ─────────────────────────────────────────────────────────────────────────────

export const getGroupsForAdvance = async () => {
    try {
        const response = await axios.get(`${base}/HR/GetGroupsForAdvance`);
        return response.data;
    } catch (error) {
        if (error.response?.data) throw error.response.data;
        throw error.message || 'Failed to fetch groups';
    }
};

export const getAllAdvanceRuleData = async (Status = 'Approved') => {
    try {
        const response = await axios.get(`${base}/HR/GetAllAdvanceRuleData`, { params: { Status } });
        return response.data;
    } catch (error) {
        if (error.response?.data) throw error.response.data;
        throw error.message || 'Failed to fetch advance rules';
    }
};

export const saveHRAdvanceRule = async ({ AdvanceRuleData, CreatedBy }) => {
    try {
        const response = await axios.post(`${base}/HR/SaveHRAdvanceRule`, { AdvanceRuleData, CreatedBy });
        return response.data;
    } catch (error) {
        if (error.response?.data) throw error.response.data;
        throw error.message || 'Failed to save advance rule';
    }
};

export const deleteAdvanceRule = async ({ AdvanceRuleData, CreatedBy }) => {
    try {
        const response = await axios.put(`${base}/HR/DeleteAdvanceRule`, { AdvanceRuleData, CreatedBy });
        return response.data;
    } catch (error) {
        if (error.response?.data) throw error.response.data;
        throw error.message || 'Failed to delete advance rule';
    }
};

// ─────────────────────────────────────────────────────────────────────────────
// 9. NOTICE PERIOD
// ─────────────────────────────────────────────────────────────────────────────

export const getGroupsForNoticePeriod = async () => {
    try {
        const response = await axios.get(`${base}/HR/GetGroupsForNoticePeriod`);
        return response.data;
    } catch (error) {
        if (error.response?.data) throw error.response.data;
        throw error.message || 'Failed to fetch groups';
    }
};

export const getNotificationToRole = async () => {
    try {
        const response = await axios.get(`${base}/HR/GetNotificationToRole`);
        return response.data;
    } catch (error) {
        if (error.response?.data) throw error.response.data;
        throw error.message || 'Failed to fetch notification roles';
    }
};

export const getNoticePeriodData = async () => {
    try {
        const response = await axios.get(`${base}/HR/GetNoticePeriodData`);
        return response.data;
    } catch (error) {
        if (error.response?.data) throw error.response.data;
        throw error.message || 'Failed to fetch notice period rules';
    }
};

export const saveHRNoticePeriodRule = async ({ EmployeeNoticePeriodRulesData, CreatedBy }) => {
    try {
        const response = await axios.post(`${base}/HR/SaveHRNoticePeriodRule`, { EmployeeNoticePeriodRulesData, CreatedBy });
        return response.data;
    } catch (error) {
        if (error.response?.data) throw error.response.data;
        throw error.message || 'Failed to save notice period rule';
    }
};

export const deleteNoticePeriod = async ({ EmployeeNoticePeriodRulesData, CreatedBy }) => {
    try {
        const response = await axios.put(`${base}/HR/DeleteNoticePeriod`, { EmployeeNoticePeriodRulesData, CreatedBy });
        return response.data;
    } catch (error) {
        if (error.response?.data) throw error.response.data;
        throw error.message || 'Failed to delete notice period rule';
    }
};

// ─────────────────────────────────────────────────────────────────────────────
// 10. TAX (Professional Tax)
// ─────────────────────────────────────────────────────────────────────────────

export const getGroupsForTaxRule = async () => {
    try {
        const response = await axios.get(`${base}/HR/GetGroupsForTaxRule`);
        return response.data;
    } catch (error) {
        if (error.response?.data) throw error.response.data;
        throw error.message || 'Failed to fetch groups';
    }
};

export const getStatesListForTax = async (GroupId) => {
    try {
        const response = await axios.get(`${base}/HR/GetStatesListForTax`, { params: { GroupId } });
        return response.data;
    } catch (error) {
        if (error.response?.data) throw error.response.data;
        throw error.message || 'Failed to fetch states for tax rule';
    }
};

export const getProfessionalTaxData = async () => {
    try {
        const response = await axios.get(`${base}/HR/GetProfessionalTaxData`);
        return response.data;
    } catch (error) {
        if (error.response?.data) throw error.response.data;
        throw error.message || 'Failed to fetch professional tax rules';
    }
};

export const saveHRTaxRule = async (payload) => {
    try {
        const response = await axios.post(`${base}/HR/SaveHRTaxRule`, payload);
        return response.data;
    } catch (error) {
        if (error.response?.data) throw error.response.data;
        throw error.message || 'Failed to save tax rule';
    }
};

export const deleteProfessionalTax = async (payload) => {
    try {
        const response = await axios.put(`${base}/HR/DeleteProfessionalTax`, payload);
        return response.data;
    } catch (error) {
        if (error.response?.data) throw error.response.data;
        throw error.message || 'Failed to delete tax rule';
    }
};

// ─────────────────────────────────────────────────────────────────────────────
// 11. OFFICIAL TRAVEL
// ─────────────────────────────────────────────────────────────────────────────

export const getGroupsForTravelRule = async () => {
    try {
        const response = await axios.get(`${base}/HR/GetGroupsForTravelRule`);
        return response.data;
    } catch (error) {
        if (error.response?.data) throw error.response.data;
        throw error.message || 'Failed to fetch groups';
    }
};

export const getAllHRTravelExpRules = async (Type = 'CurrentlyActive') => {
    try {
        const response = await axios.get(`${base}/HR/GetAllHRTravelExpRules`, { params: { Type } });
        return response.data;
    } catch (error) {
        if (error.response?.data) throw error.response.data;
        throw error.message || 'Failed to fetch travel expense rules';
    }
};

export const saveHRTravelRule = async (payload) => {
    try {
        const response = await axios.post(`${base}/HR/SaveHRTravelRule`, payload);
        return response.data;
    } catch (error) {
        if (error.response?.data) throw error.response.data;
        throw error.message || 'Failed to save travel expense rule';
    }
};

export const deleteHRTravelExpRule = async (payload) => {
    try {
        const response = await axios.put(`${base}/HR/DeleteHRTravelExpRule`, payload);
        return response.data;
    } catch (error) {
        if (error.response?.data) throw error.response.data;
        throw error.message || 'Failed to delete travel expense rule';
    }
};
